import { useState } from "react";
import { useNavigate, useLocation, NavLink } from "react-router-dom";
import TendoLogo from "../assets/images/tendo-logo.png";
import Search from "./Search";
import { useMediaQuery } from "react-responsive";
import { ArrowLeft, ShoppingBag, MessageCircle } from "lucide-react";
import { useAppSelector } from "../redux/hook";
import LgAccountBtn from "./LgAccountBtn";
import { uniqueId } from "lodash";
import { useFreshDeskScript } from "../hooks/useFreshdeskScript";
import { initFreshChat } from "../services/freshchat.service";
import useCartItemCount from "../pages/Cart/v2/hooks/useCartItemCount";

const Header = ({ title, showBack, search, goBack, data }) => {
  const cartItemCount = useCartItemCount();
  const { user } = useAppSelector((state) => state.auth);
  const history = useNavigate();
  const _goBack = () => history(-1);

  const lgNav = [
    {
      title: "Home",
      path: "/",
      auth: false,
    },
    {
      title: "Categories",
      path: "/categories",
      auth: false,
    },
    {
      title: "Shop",
      path: "/shop",
      auth: true,
    },
    {
      title: "Explore",
      path: "/explore",
      auth: false,
    },
  ];

  const [isLoadedFreshChat, setLoadedFreshChat] = useState(false);

  function openFreshChat() {
    const fcWidget = window.fcWidget;
    if (fcWidget && fcWidget.isLoaded()) {
      fcWidget.user.setFirstName(user ? user.name.split(" ")[0] : "");
      fcWidget.user.setLastName(user ? user.name.split(" ")[1] : "");
      fcWidget.setExternalId(user ? user.username : uniqueId());
      fcWidget.user.setEmail(user ? user.email : "");
      fcWidget.user.setPhone(user ? user.phone : "");

      fcWidget.show();
      fcWidget.open();
    }
  }

  const isTabletOrMobile = useMediaQuery({ maxWidth: 1224 });
  const locations = useLocation();
  const currentPath = locations.pathname.includes("/cart");
  const isAccountPage = locations.pathname.includes("/accounts");

  useFreshDeskScript(
    !isAccountPage
      ? () => {
          initFreshChat();
          const fcWidget = window.fcWidget;
          fcWidget.on("widget:loaded", () => fcWidget.hide());
          setLoadedFreshChat(true);
          fcWidget.on("widget:closed", () => fcWidget.hide());
        }
      : () => null
  );

  const CartButton = () => (
    <button
      type="button"
      className="relative"
      onClick={() => history("/cart")}
      aria-label="Cart"
    >
      <ShoppingBag className="h-5 w-5 text-gray-800" />
      {cartItemCount > 0 && (
        <span className="absolute -top-1.5 -right-1.5 bg-buttonPrimary text-white text-[10px] min-w-[16px] h-4 px-1 rounded-full flex items-center justify-center">
          {cartItemCount}
        </span>
      )}
    </button>
  );

  return (
    <div className="bg-white py-3 flex lg:items-center flex-col lg:flex-row fixed z-10 top-0 w-full px-4 border-b border-gray-100 font-nunito">
      <div className="flex items-center w-full justify-between">
        <div className="flex justify-center items-center mr-1">
          {showBack ? (
            <button
              type="button"
              className="flex items-center text-gray-800"
              onClick={goBack ? goBack : _goBack}
              aria-label="Go back"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
          ) : (
            <img
              src={TendoLogo}
              alt="tendo logo"
              height={25}
              width={25}
              className="rounded-sm"
            />
          )}
        </div>
        <div className="text-center flex-1 lg:mx-auto lg:flex lg:justify-center lg:items-center">
          <span className="text-gray-800 font-semibold truncate w-52 text-base lg:hidden block">
            {title}
          </span>
          <nav className="hidden lg:flex space-x-8">
            {lgNav.map(
              (nav) =>
                (!nav.auth || (user && nav.auth)) && (
                  <NavLink
                    key={nav.title}
                    to={nav.path}
                    className={({ isActive }) =>
                      isActive
                        ? "text-gray-900 font-semibold text-sm"
                        : "text-sm text-gray-600 font-medium hover:text-gray-900"
                    }
                  >
                    {nav.title}
                  </NavLink>
                )
            )}
          </nav>
        </div>
        {isTabletOrMobile && !currentPath && <CartButton />}
        {isTabletOrMobile && !isAccountPage && (
          <button
            type="button"
            className="ml-3"
            aria-label="Help"
            onClick={() => {
              if (isLoadedFreshChat) {
                openFreshChat();
              }
            }}
          >
            <MessageCircle className="h-5 w-5 text-gray-800" />
          </button>
        )}
      </div>
      {search && (
        <div className="flex w-full lg:w-96 px-2 mt-3 lg:mt-0">
          <Search data={data} onSearch={search} />
        </div>
      )}
      {!isTabletOrMobile && (
        <div className="flex items-center space-x-4 ml-4">
          {!currentPath && <CartButton />}
          <button
            type="button"
            aria-label="Help"
            onClick={() => {
              if (isLoadedFreshChat) {
                openFreshChat();
              }
            }}
          >
            <MessageCircle className="h-5 w-5 text-gray-800" />
          </button>
          {user ? (
            <LgAccountBtn />
          ) : (
            <button
              type="button"
              className="text-sm font-medium text-blue-500"
              onClick={() => history("/auth/login")}
            >
              Sign in
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Header;
